import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import Modal from '../components/Modal';
const REPORT_TRANSLATIONS = {
    fr: {
        docTitle: "Rapport des Achats Locaux",
        companySub: "Societe d'importation et de commerce",
        supermarket: "SUPERMARCHE Yazbek 🛒",
        supplierLabel: "FOURNISSEUR",
        supplierDefault: "Fr. DIRECTION Yazbek / Tous",
        reportingPeriod: "PÉRIODE DU RAPPORT",
        reportDate: "DATE DU RAPPORT",
        qty: "QTÉ",
        noPurchases: "Aucun achat trouvé",
        colQty: "QTY",
        colDesignations: "DESIGNATIONS",
        colPrixVtesLd: "PRIX DE VTES LD",
        colTotVtesLd: "TOTAL P.VTES LD",
        colMarge: "MARGE",
        colTva: "TVA",
        colPrixRevTtc: "PRIX DE REV TTC",
        colTotPrevLd: "TOT PREV LD",
        colPrixAchDr: "PRIX ACH DR",
        colTotAchDr: "TOTAL P.ACH DR",
        colBenefLd: "BENEF LD",
        colBenefDr: "BENEF DR",
        colTotBenef: "TOT BENEF",
        totals: "TOTALS",
        coutAch: "COUT ACH",
        coutLd: "COUT LD",
        benefLd: "BENEF LD",
        total: "TOTAL",
        achatDirection: "ACHAT DIRECTION",
        achatLocal: "ACHAT LOCAL",
        previewTitle: "Aperçu du Rapport des Achats",
        printBtn: "Imprimer PDF",
        loading: "Chargement...",
        selectLang: "Choisir la langue du PDF"
    },
    en: {
        docTitle: "Local Purchases Report",
        companySub: "Import & Trading Company",
        supermarket: "SUPERMARKET Yazbek 🛒",
        supplierLabel: "SUPPLIER",
        supplierDefault: "Direction Yazbek / All",
        reportingPeriod: "REPORTING PERIOD",
        reportDate: "REPORT DATE",
        qty: "QTY",
        noPurchases: "No purchases found",
        colQty: "QTY",
        colDesignations: "DESCRIPTION",
        colPrixVtesLd: "SELLING PRICE LD",
        colTotVtesLd: "TOTAL SALES LD",
        colMarge: "COST BASE (HT)",
        colTva: "VAT",
        colPrixRevTtc: "UNIT COST (TTC)",
        colTotPrevLd: "TOTAL COST LD",
        colPrixAchDr: "PURCHASE PRICE DR",
        colTotAchDr: "TOTAL PURCHASE DR",
        colBenefLd: "PROFIT LD (80%)",
        colBenefDr: "PROFIT DR (20%)",
        colTotBenef: "TOTAL PROFIT",
        totals: "TOTALS",
        coutAch: "PURCHASE COST",
        coutLd: "STORE COST (LD)",
        benefLd: "STORE PROFIT (LD)",
        total: "TOTAL",
        achatDirection: "HQ PURCHASES (DIRECTION)",
        achatLocal: "LOCAL PURCHASES",
        previewTitle: "Purchases Report Preview",
        printBtn: "Print PDF",
        loading: "Loading...",
        selectLang: "Select PDF Language"
    }
};

const formatSupplierSource = (source, lang) => {
    if (source === 'ACHAT LOCAL') {
        return lang === 'en' ? 'LOCAL PURCHASES' : 'ACHAT LOCAL';
    }
    if (source === 'ACHAT DIRECTION') {
        return lang === 'en' ? 'HQ PURCHASES (DIRECTION)' : 'ACHAT DIRECTION';
    }
    return source;
};

const PurchasesList = () => {
    const navigate = useNavigate();
    const { authFetch, user } = useContext(AppContext);
    const { t } = useTranslation();
    const [purchases, setPurchases] = useState([]);
    const today = new Date().toISOString().slice(0, 10);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('All');
    const [selectedGroupModal, setSelectedGroupModal] = useState(null);

    // View/Edit state
    const [viewPurchase, setViewPurchase] = useState(null);

    // Print State & Language Selection
    const [detailedPrintData, setDetailedPrintData] = useState(null);
    const [isPrinting, setIsPrinting] = useState(false);
    const [printLang, setPrintLang] = useState('fr');
    const [showPrintDropdown, setShowPrintDropdown] = useState(false);
    const printDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (printDropdownRef.current && !printDropdownRef.current.contains(e.target)) {
                setShowPrintDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const reportTx = REPORT_TRANSLATIONS[printLang] || REPORT_TRANSLATIONS.fr;

    const isAdmin = user?.role === 'Admin' || user?.role_name === 'Super Admin';
    const isManager = user?.role === 'Manager' || user?.role === 'Controller';
    const isWarehouse = user?.role === 'Chef Depo';

    const canDelete = isAdmin;
    const canEdit = isAdmin;
    const canPrint = isAdmin || isManager;

    const fetchPurchases = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
            const res = await authFetch(`/api/purchases?${params}`);
            const data = await res.json();
            setPurchases(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchases();
    }, [startDate, endDate]);

    const handleDelete = async (id) => {
        if (!canDelete) return;
        if (window.confirm(t('Are you sure you want to delete this purchase? Stock will be reversed.'))) {
            try {
                const res = await authFetch(`/api/purchases/${id}`, { method: 'DELETE' });
                if (res.ok) {
                    fetchPurchases();
                } else {
                    const d = await res.json();
                    alert(d.message || t('Failed to delete'));
                }
            } catch (e) {
                alert(t('Network error'));
            }
        }
    };

    const handleApprove = async (id) => {
        if (!isAdmin) return;
        if (window.confirm(t('Approve this purchase? Stock will be updated.'))) {
            try {
                const res = await authFetch(`/api/purchases/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ status: 'Paid' })
                });
                if (res.ok) {
                    fetchPurchases();
                } else {
                    const d = await res.json();
                    alert(d.message || t('Failed to approve'));
                }
            } catch (e) {
                alert(t('Network error'));
            }
        }
    };

    const handlePrintDailyReport = async (chosenLang) => {
        const langToUse = chosenLang || printLang;
        setPrintLang(langToUse);
        setIsPrinting(true);
        try {
            const params = new URLSearchParams({ start_date: startDate, end_date: endDate, detailed: 1 });
            const res = await authFetch(`/api/purchases?${params}`);
            const data = await res.json();

            const groups = {};
            let totalQty = 0;
            const supplierCostTotals = {};
            let grandTotalCost = 0;
            let grandTotalRetail = 0;

            if (Array.isArray(data)) {
                data.forEach(purchase => {
                    const src = purchase.purchase_type === 'Local Purchase' ? 'ACHAT LOCAL' : (purchase.supplier || purchase.supplier_name || 'ACHAT DIRECTION');

                    if (Array.isArray(purchase.items)) {
                        purchase.items.forEach(item => {
                            const vat = parseFloat(item.vat_rate || 0);
                            const vatKey = `All Items`;
                            if (!groups[vatKey]) groups[vatKey] = { vat_rate: vat, items: [], total_sales: 0, total_cost_ht: 0, total_cost_ttc: 0, total_margin: 0 };

                            const qty = parseFloat(item.qty || 0);
                            totalQty += qty;
                            const costHT = parseFloat(item.cost_price || 0);
                            const costTTC = costHT * (1 + vat / 100);
                            const retail = parseFloat(item.price || 0); // from pr.price

                            supplierCostTotals[src] = (supplierCostTotals[src] || 0) + (qty * costHT);

                            const benef = (qty * retail) - (qty * costHT);
                            const benefLD = benef * 0.8; // Approximate layout split
                            const benefDR = benef * 0.2; // Approximate layout split

                            groups[vatKey].items.push({
                                ...item,
                                qty,
                                retail_price: retail,
                                total_retail: qty * retail,
                                marge: costTTC,
                                tva_amt: costTTC - costHT,
                                cost_ttc: costTTC,
                                total_cost_ttc: costTTC * qty,
                                cost_ht: costHT,
                                total_cost_ht: costHT * qty,
                                benef_ld: benefLD,
                                benef_dr: benefDR,
                                tot_benef: benef
                            });

                            groups[vatKey].total_sales += qty * retail;
                            groups[vatKey].total_cost_ht += qty * costHT;
                            groups[vatKey].total_cost_ttc += qty * costTTC;
                            groups[vatKey].total_margin += costTTC;

                            grandTotalCost += qty * costHT;
                            grandTotalRetail += qty * retail;
                        });
                    }
                });
            }

            setDetailedPrintData({
                groups,
                totalQty,
                supplierCostTotals,
                grandTotalCost,
                grandTotalRetail,
                totalBenef: grandTotalRetail - grandTotalCost
            });

            setTimeout(() => {
                window.print();
                setIsPrinting(false);
            }, 800);
        } catch (e) {
            console.error(e);
            alert("Failed to load print details.");
            setIsPrinting(false);
        }
    };

    const handleView = async (purchase) => {
        try {
            const res = await authFetch(`/api/purchases/${purchase.id}`);
            const data = await res.json();
            setViewPurchase(data);
        } catch (e) {
            console.error(e);
        }
    };

    const filteredPurchases = purchases.filter(p => statusFilter === 'All' || p.status === statusFilter);

    const groupedPurchases = filteredPurchases.reduce((acc, p) => {
        let groupKey;
        if (p.purchase_type === 'Local Purchase') {
            groupKey = 'Local Purchases';
        } else {
            groupKey = `Supplier: ${p.supplier || p.supplier_name || 'Unknown'}`;
        }

        if (!acc[groupKey]) {
            acc[groupKey] = [];
        }
        acc[groupKey].push(p);
        return acc;
    }, {});

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-8">
            <div className="flex justify-between items-center mb-6 no-print">
                <h1 className="text-2xl font-bold text-gray-800">
                    {startDate === endDate ? t("Purchases") + ` — ${startDate}` : t("Purchases") + ` — ${startDate} → ${endDate}`}
                </h1>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                        <span className="text-xs font-semibold text-gray-500 uppercase">{t('From')}</span>
                        <input
                            type="date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            className="p-1 border-0 outline-none text-sm text-gray-700 bg-transparent"
                        />
                        <span className="text-gray-400">→</span>
                        <span className="text-xs font-semibold text-gray-500 uppercase">{t('To')}</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            className="p-1 border-0 outline-none text-sm text-gray-700 bg-transparent"
                        />
                    </div>
                    {canPrint && (
                        <div className="flex items-center gap-2">
                            {/* Language Switcher Pill */}
                            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-0.5 shadow-sm">
                                <button
                                    type="button"
                                    onClick={() => setPrintLang('fr')}
                                    className={`px-2.5 py-1 text-xs font-bold rounded transition-colors flex items-center gap-1 ${
                                        printLang === 'fr'
                                            ? 'bg-amber-500 text-white shadow-xs'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                    title="Imprimer en Français"
                                >
                                    <span>🇫🇷</span> FR
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPrintLang('en')}
                                    className={`px-2.5 py-1 text-xs font-bold rounded transition-colors flex items-center gap-1 ${
                                        printLang === 'en'
                                            ? 'bg-indigo-600 text-white shadow-xs'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                    title="Print in English"
                                >
                                    <span>🇬🇧</span> EN
                                </button>
                            </div>

                            {/* Print Button with Language Dropdown */}
                            <div className="relative inline-flex shadow-sm rounded-lg" ref={printDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => handlePrintDailyReport(printLang)}
                                    disabled={isPrinting}
                                    className="bg-indigo-600 text-white px-3.5 py-2 rounded-l-lg hover:bg-indigo-700 flex items-center gap-2 text-sm font-semibold transition disabled:opacity-50"
                                >
                                    {isPrinting ? (
                                        <span>⏳ {reportTx.loading}</span>
                                    ) : (
                                        <>
                                            <span>🖨️</span>
                                            <span>{printLang === 'fr' ? 'Imprimer PDF' : 'Print PDF'}</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowPrintDropdown(!showPrintDropdown)}
                                    disabled={isPrinting}
                                    className="bg-indigo-700 text-white px-2 py-2 rounded-r-lg hover:bg-indigo-800 border-l border-indigo-500 flex items-center transition disabled:opacity-50 cursor-pointer"
                                    title="Select PDF Language"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
                                </button>

                                {showPrintDropdown && (
                                    <div className="absolute right-0 top-full mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50 text-xs font-medium">
                                        <div className="px-3 py-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                            {reportTx.selectLang}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowPrintDropdown(false);
                                                handlePrintDailyReport('fr');
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-amber-50 hover:text-amber-900 flex items-center justify-between text-gray-700 transition cursor-pointer"
                                        >
                                            <span className="flex items-center gap-2">
                                                <span className="text-base">🇫🇷</span>
                                                <span className="font-bold">Français</span>
                                            </span>
                                            <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-mono font-bold">FR</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowPrintDropdown(false);
                                                handlePrintDailyReport('en');
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-indigo-50 hover:text-indigo-900 flex items-center justify-between text-gray-700 transition cursor-pointer"
                                        >
                                            <span className="flex items-center gap-2">
                                                <span className="text-base">🇬🇧</span>
                                                <span className="font-bold">English</span>
                                            </span>
                                            <span className="text-[10px] bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded font-mono font-bold">EN</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Status Filter Buttons */}
            <div className="flex gap-2 mb-4 no-print">
                {['All', 'Paid', 'Draft'].map(status => (
                    <button
                        key={status}
                        onClick={() => setStatusFilter(status)}
                        className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors
                            ${statusFilter === status
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                            }`}
                    >
                        {status === 'All' ? t('All') : status === 'Paid' ? t('Paid') : t('Draft')}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden no-print">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-100 border-b">
                            <tr>
                                <th className="px-6 py-3">{t('Invoice')}</th>
                                <th className="px-6 py-3">{t('Supplier')}</th>
                                <th className="px-6 py-3">{t('Type')}</th>
                                <th className="px-6 py-3">{t('Cost')}</th>
                                <th className="px-6 py-3">{t('Created By')}</th>
                                <th className="px-6 py-3">{t('Time')}</th>
                                <th className="px-6 py-3">{t('Status')}</th>
                                <th className="px-6 py-3 text-right">{t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8" className="text-center py-8">{t('Loading...')}</td></tr>
                            ) : Object.keys(groupedPurchases).length === 0 ? (
                                <tr><td colSpan="8" className="text-center py-8">{t('No purchases found')}</td></tr>
                            ) : (
                                Object.entries(groupedPurchases).map(([groupName, items]) => {
                                    const groupTotalCost = items.reduce((sum, p) => sum + parseFloat(p.grand_total || p.total_cost || 0), 0);

                                    return (
                                        <tr
                                            key={groupName}
                                            className="bg-indigo-50 border-b cursor-pointer hover:bg-indigo-100 transition-colors"
                                            onClick={() => setSelectedGroupModal(groupName)}
                                        >
                                            <td colSpan="3" className="px-6 py-3 font-extrabold text-indigo-900 flex items-center gap-2">
                                                <span className="text-indigo-500 w-4 inline-block">▶</span>
                                                {groupName} <span className="bg-white text-indigo-600 px-2 py-0.5 rounded-full text-xs ml-2 shadow-sm">{items.length}</span>
                                            </td>
                                            <td className="px-6 py-3 font-black text-indigo-900">
                                                {groupTotalCost.toFixed(2)}
                                            </td>
                                            <td colSpan="4"></td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Print View (Only visible on print) */}
            {detailedPrintData && (
                <div className="hidden print:block print-container text-black bg-white" style={{ fontFamily: 'Arial, sans-serif' }}>
                    <div className="flex gap-4 items-center mb-6 border-b-2 border-[#1a4f8a] pb-4">
                        <div className="w-24 h-24 border-[3px] border-[#3eb56b] rounded-full flex flex-col items-center justify-center font-bold text-xs bg-white text-[#1a4f8a] overflow-hidden">
                            {/* Abstract logo shape */}
                            <div className="text-4xl">⮏⮎</div>
                        </div>
                        <div>
                            <h1 className="text-5xl font-black text-[#1a4f8a] tracking-tight m-0">Yazbek</h1>
                            <p className="text-lg text-gray-700 m-0 leading-tight">{reportTx.companySub}</p>
                            <h2 className="text-2xl font-bold tracking-widest mt-2 uppercase text-[#1a4f8a] flex items-center gap-2">
                                {reportTx.supermarket}
                            </h2>
                        </div>
                    </div>

                    <h2 className="text-3xl font-bold mb-4 font-serif">{reportTx.docTitle}</h2>

                    <div className="w-1/2 text-xs font-semibold uppercase mb-8 bg-gray-50 border-y border-black p-2">
                        <div className="grid grid-cols-2 py-1"><span className="text-gray-500">{reportTx.supplierLabel}</span> <span>{reportTx.supplierDefault}</span></div>
                        <div className="grid grid-cols-2 py-1"><span className="text-gray-500">{reportTx.reportingPeriod}</span> <span>{startDate.replace(/-/g, '/')} — {endDate.replace(/-/g, '/')}</span></div>
                        <div className="grid grid-cols-2 py-1"><span className="text-gray-500">{reportTx.reportDate}</span> <span>{new Date().toLocaleString(printLang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '')}</span></div>
                        <div className="grid grid-cols-2 py-1"><span className="text-gray-500">{reportTx.qty}</span> <span>{detailedPrintData.totalQty}</span></div>
                    </div>

                    {Object.entries(detailedPrintData.groups).map(([vatKey, group]) => (
                        <div key={vatKey} className="mb-10 page-break-inside-avoid">
                            <div className="w-full overflow-x-auto max-w-full">
                                <table className="w-full text-[10px] border-collapse mt-4">
                                    <thead>
                                        <tr className="bg-[#1a4f8a] text-white uppercase leading-tight">
                                            <th className="p-1.5 border border-white text-center w-8">{reportTx.colQty}</th>
                                            <th className="p-1.5 border border-white text-left w-48">{reportTx.colDesignations}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colPrixVtesLd}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTotVtesLd}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colMarge}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTva}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colPrixRevTtc}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTotPrevLd}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colPrixAchDr}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTotAchDr}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colBenefLd}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colBenefDr}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTotBenef}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.items.length === 0 && (
                                            <tr><td colSpan="13" className="text-center p-2 text-gray-500 border border-gray-200">{reportTx.noPurchases}</td></tr>
                                        )}
                                        {group.items.map((item, idx) => (
                                            <tr key={idx} className="border-b border-gray-300">
                                                <td className="p-1.5 text-center bg-gray-50">{item.qty}</td>
                                                <td className="p-1.5 uppercase font-medium">{item.product_name}</td>
                                                <td className="p-1.5 text-right text-gray-600">{item.retail_price.toFixed(2)} CFA</td>
                                                <td className="p-1.5 text-right text-gray-600">{item.total_retail.toFixed(2)} CFA</td>
                                                <td className="p-1.5 text-right text-gray-600">{item.marge.toFixed(2)} CFA</td>
                                                <td className="p-1.5 text-right text-gray-500 leading-none">
                                                    <div>{group.vat_rate.toFixed(2)}%</div>
                                                    <div className="mt-1">{item.tva_amt.toFixed(2)} CFA</div>
                                                </td>
                                                <td className="p-1.5 text-right">{item.cost_ttc.toFixed(8)} CFA</td>
                                                <td className="p-1.5 text-right">{item.total_cost_ttc.toFixed(3)} CFA</td>
                                                <td className="p-1.5 text-right">{item.cost_ht.toFixed(2)} CFA</td>
                                                <td className="p-1.5 text-right">{item.total_cost_ht.toFixed(2)} CFA</td>
                                                <td className="p-1.5 text-right text-gray-500">{item.benef_ld.toFixed(2)} CFA</td>
                                                <td className="p-1.5 text-right text-gray-500">{item.benef_dr.toFixed(2)} CFA</td>
                                                <td className="p-1.5 text-right font-medium">{item.tot_benef.toFixed(2)} CFA</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="font-bold bg-gray-100 border-y-2 border-gray-400">
                                            <td className="p-1.5 text-center">{group.items.reduce((s, i) => s + i.qty, 0)}</td>
                                            <td className="p-1.5">{reportTx.totals}</td>
                                            <td className="p-1.5 text-center">-</td>
                                            <td className="p-1.5 text-right">{group.total_sales.toFixed(3)} CFA</td>
                                            <td className="p-1.5 text-right">{group.total_margin.toFixed(3)} CFA</td>
                                            <td className="p-1.5 text-right">{(group.total_cost_ttc - group.total_cost_ht).toFixed(3)} CFA</td>
                                            <td className="p-1.5 text-center">-</td>
                                            <td className="p-1.5 text-right">{group.total_cost_ttc.toFixed(3)} CFA</td>
                                            <td className="p-1.5 text-center">-</td>
                                            <td className="p-1.5 text-right">{group.total_cost_ht.toFixed(3)} CFA</td>
                                            <td className="p-1.5 text-right text-gray-500">{((group.total_sales - group.total_cost_ht) * 0.8).toFixed(3)} CFA</td>
                                            <td className="p-1.5 text-right text-gray-500">{((group.total_sales - group.total_cost_ht) * 0.2).toFixed(3)} CFA</td>
                                            <td className="p-1.5 text-right">{(group.total_sales - group.total_cost_ht).toFixed(3)} CFA</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    ))}

                    {/* Fallback if no groups exist for UI completeness */}
                    {Object.keys(detailedPrintData.groups).length === 0 && (
                        <div className="mb-10 page-break-inside-avoid">
                            <h3 className="font-bold text-xl mb-1">VAT 5%</h3>
                            <div className="w-full overflow-x-auto max-w-full">
                                <table className="w-full text-[10px] border-collapse">
                                    <thead>
                                        <tr className="bg-[#1a4f8a] text-white uppercase leading-tight">
                                            <th className="p-1.5 border border-white text-center w-8">{reportTx.colQty}</th>
                                            <th className="p-1.5 border border-white text-left w-48">{reportTx.colDesignations}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colPrixVtesLd}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTotVtesLd}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colMarge} 5.00%</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTva}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colPrixRevTtc}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTotPrevLd}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colPrixAchDr}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTotAchDr}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colBenefLd}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colBenefDr}</th>
                                            <th className="p-1.5 border border-white text-right">{reportTx.colTotBenef}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr><td colSpan="13" className="text-center p-2 text-gray-500 border border-gray-200">{reportTx.noPurchases}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Final Totals Table */}
                    <div className="mt-12 page-break-inside-avoid w-[60%] ml-auto text-[11px]">
                        <div className="w-full overflow-x-auto max-w-full">
                            <table className="w-full mb-4 border border-black border-collapse bg-white">
                                <tbody>
                                    {Object.keys(detailedPrintData.supplierCostTotals).length > 0 ? (
                                        Object.entries(detailedPrintData.supplierCostTotals).map(([source, total]) => (
                                            <tr key={source}>
                                                <td className="p-1.5 border border-black uppercase text-gray-600">
                                                    {formatSupplierSource(source, printLang)}
                                                </td>
                                                <td className="p-1.5 border border-black text-right">{total.toFixed(3)} CFA</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td className="p-1.5 border border-black uppercase text-gray-600">
                                                {reportTx.achatDirection}
                                            </td>
                                            <td className="p-1.5 border border-black text-right">0.000 CFA</td>
                                        </tr>
                                    )}

                                    <tr className="font-bold">
                                        <td className="p-1.5 border border-black uppercase">{reportTx.total}</td>
                                        <td className="p-1.5 border border-black text-right">{detailedPrintData.grandTotalCost.toFixed(3)} CFA</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-between items-start mt-6">
                            <div className="w-2/3"></div>
                            <div className="w-1/3 min-w-[200px]">
                                <div className="w-full overflow-x-auto max-w-full">
                                    <table className="w-full font-bold border border-black border-collapse mb-20 bg-gray-100">
                                        <tbody>
                                            <tr>
                                                <td className="p-1.5 border border-black text-gray-700">{reportTx.coutAch}</td>
                                                <td className="p-1.5 border border-black text-right">{detailedPrintData.grandTotalCost.toFixed(3)} CFA</td>
                                            </tr>
                                            <tr>
                                                <td className="p-1.5 border border-black text-gray-700">{reportTx.coutLd}</td>
                                                <td className="p-1.5 border border-black text-right">{detailedPrintData.grandTotalCost.toFixed(3)} CFA</td>
                                            </tr>
                                            <tr>
                                                <td className="p-1.5 border border-black text-gray-700">{reportTx.benefLd}</td>
                                                <td className="p-1.5 border border-black text-right">{detailedPrintData.totalBenef.toFixed(3)} CFA</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Group Detail Modal */}
            <Modal size="6xl" isOpen={!!selectedGroupModal} onClose={() => setSelectedGroupModal(null)} title={selectedGroupModal || ''}>
                {selectedGroupModal && groupedPurchases[selectedGroupModal] && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-100 border-b">
                                <tr>
                                    <th className="px-4 py-3">{t('Invoice')}</th>
                                    <th className="px-4 py-3">{t('Supplier')}</th>
                                    <th className="px-4 py-3">{t('Type')}</th>
                                    <th className="px-4 py-3">{t('Cost')}</th>
                                    <th className="px-4 py-3">{t('Created By')}</th>
                                    <th className="px-4 py-3">{t('Time')}</th>
                                    <th className="px-4 py-3">{t('Status')}</th>
                                    <th className="px-4 py-3 text-right">{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedPurchases[selectedGroupModal].map(p => (
                                    <tr key={p.id} className="border-b hover:bg-gray-50 bg-white">
                                        <td className="px-4 py-3 font-bold">#{p.invoice_number || p.id}</td>
                                        <td className="px-4 py-3">{p.supplier || p.supplier_name || 'N/A'}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold shadow-sm ${p.purchase_type === 'Local Purchase' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                                                {p.purchase_type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 font-bold text-gray-700">{parseFloat(p.grand_total || p.total_cost || 0).toFixed(2)}</td>
                                        <td className="px-4 py-3 text-gray-600">{p.created_by_name || 'System'}</td>
                                        <td className="px-4 py-3 text-gray-600 font-medium">{new Date(p.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm ${p.status === 'Draft' ? 'bg-amber-100 text-amber-800 border border-amber-200' : p.status === 'Paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-blue-100 text-blue-800 border border-blue-200'}`}>
                                                {p.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right flex justify-end gap-2">
                                            {p.status === 'Draft' && isAdmin && (
                                                <button onClick={() => { setSelectedGroupModal(null); handleApprove(p.id); }} className="text-emerald-600 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 p-2 rounded transition-colors" title={t('Approve')}>✅</button>
                                            )}
                                            <button onClick={() => { setSelectedGroupModal(null); handleView(p); }} className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 p-2 rounded transition-colors" title={t('View')}>👁</button>
                                            {canEdit && (
                                                <button onClick={() => { setSelectedGroupModal(null); navigate(`/purchases/edit/${p.id}`); }} className="text-orange-500 hover:text-orange-700 bg-orange-50 hover:bg-orange-100 p-2 rounded transition-colors" title={t('Edit')}>✏️</button>
                                            )}
                                            <button onClick={() => alert("Single print logic not updated yet.")} className="text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 p-2 rounded transition-colors" title={t('Print')}>🖨</button>
                                            <button onClick={() => { setSelectedGroupModal(null); handleDelete(p.id); }} className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-2 rounded transition-colors" title={t('Delete')}>🗑</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>

            {/* View Modal */}
            <Modal isOpen={!!viewPurchase} onClose={() => setViewPurchase(null)} title={`${t('Purchase')} #${viewPurchase?.invoice_number || viewPurchase?.id}`}>
                {viewPurchase && (
                    <div>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div><strong>{t('Supplier')}:</strong> {viewPurchase.supplier_name || 'N/A'}</div>
                            <div><strong>{t('Type')}:</strong> {viewPurchase.purchase_type}</div>
                            <div><strong>{t('Date')}:</strong> {new Date(viewPurchase.created_at).toLocaleString()}</div>
                            <div><strong>{t('Created By')}:</strong> {viewPurchase.created_by_name}</div>
                        </div>
                        <div className="w-full overflow-x-auto max-w-full">
                            <table className="w-full text-sm border">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="p-2 border">{t('Item')}</th>
                                        <th className="p-2 border">{t('Qty')}</th>
                                        <th className="p-2 border">{t('Cost')}</th>
                                        <th className="p-2 border">{t('VAT %')}</th>
                                        <th className="p-2 border">{t('Total')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewPurchase.items?.map(item => (
                                        <tr key={item.id}>
                                            <td className="p-2 border">{item.product_name}</td>
                                            <td className="p-2 border text-center">{item.qty}</td>
                                            <td className="p-2 border text-right">{parseFloat(item.cost_price).toFixed(2)}</td>
                                            <td className="p-2 border text-center">{item.vat_rate}%</td>
                                            <td className="p-2 border text-right">{parseFloat(item.line_total).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th colSpan="4" className="p-2 border text-right">{t('Grand Total')}:</th>
                                        <th className="p-2 border text-right">{parseFloat(viewPurchase.grand_total || viewPurchase.total_cost).toFixed(2)}</th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PurchasesList;

